import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { FileUploadField } from '@scale/shared/types/params/textcollection';
import { FileUploadFieldAnnotation } from '@scale/shared/types/response/textcollection';
import FileUpload from 'react-scale/dashboard.scale.com/components/SelfServe/Upload/FileUpload';
import apiFetch from 'react-scale/lib/api-fetch';
import { SelfServeSupportedAttachmentMimeTypes } from '@scale/shared/defaults';
import FileSaver from 'file-saver';
import PDFView from './PDFView';
import FrameView from './FrameView';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';
import { Button, Typography, Spinner, ProgressStepper } from '@scale/scaleui';
import logger from 'react-scale/lib/logging';
import useCDSMultipartUpload from './hooks/useCDSMultipartUpload';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatAudioTranscript } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioTranscript';
import { ChatAudioEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioEditor';
import {
  ContentUnderstandingUploadResponseBody,
  SupportedAudioFormat,
  createAwsTranscriptionService,
  createWhisperTranscriptionService,
  formatMaybeErrorForLogging,
} from '@scale/mm-shared';
import { getAudioSource, processAudioAttachment } from 'react-scale/lib/chat-api/multimodal';
import retry from '@scale/shared/utils/retry';
import { toast } from 'react-toastify';
import { isAllowedMimeType } from 'react-scale/components/scalers/tasks/textcollection/fileUploadUtils';
import useLatest from 'react-scale/hooks/useLatest';
import { decodeAudioBuffer } from '@scale/frontend-lib/audio/decodeAudio';

const PREVIEW_CUTOFF_TEXT =
  '**Note that this is only a preview of your text file, please ' +
  'download the file to view the full content.**';

const PREVIEW_UNSUPPORTED_TEXT =
  '**Preview is not supported for this file type, ' +
  'please download the file to view the full content.**';

interface FileUploadFieldSectionProps {
  field: FileUploadField;
  annotation: FileUploadFieldAnnotation;
  setAnnotation: (annotation: FileUploadFieldAnnotation) => void;
  disabled?: boolean;
  assignmentId?: string;
  projectId?: string;
}

function fieldSectionAttachmentView(src: string, mimeType: string, textAttachmentContent: any) {
  if (mimeType.startsWith('audio/')) {
    return (
      <div className="flex flex-col gap-2">
        <ChatAudioEditor src={src} readonly={true} />
        <ChatAudioTranscript audioUrl={src} userGeneratedAudio={true} />
      </div>
    );
  } else if (mimeType.includes('image')) {
    return <img src={src} />;
  } else if (mimeType.includes('application/pdf')) {
    return <PDFView src={src} renderWithPDFJs={true} dontRenderDownload={true} />;
  } else if (mimeType.includes('text/html')) {
    return <FrameView src={src} />;
  } else if (mimeType.includes('text/plain')) {
    const textToPreview = textAttachmentContent.slice(0, 1000) + '\n\n' + PREVIEW_CUTOFF_TEXT;
    return <SanitizedMarkdown text={textToPreview} />;
  } else if (mimeType.includes('application/json')) {
    return (
      <ReactJsonView
        style={{
          wordBreak: 'break-all',
        }}
        src={textAttachmentContent}
      />
    );
  }

  return <SanitizedMarkdown text={PREVIEW_UNSUPPORTED_TEXT} />;
}

// Only allows for downloads and previews if the mimetype is set.
// From when #62488 is merged all files will have their mimetype stored on the response.
// The check for whether or not we have stored it is simply for backwards compatability.
// eslint-disable-next-line complexity
export default function FileUploadFieldSection(props: FileUploadFieldSectionProps) {
  const { field, annotation, setAnnotation: setAnnotationProp, disabled } = props;
  const setAnnotation = useLatest(setAnnotationProp);
  const taskContext = useContext(ChatTaskContext);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [isUploadError, setIsUploadError] = React.useState<boolean>(false);
  const [fetchedUrl, setFetchedUrl] = React.useState<string>('');
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [downloading, setDownloading] = React.useState<boolean>(false);
  const [textAttachmentContent, setTextAttachmentContent] = React.useState<string>('');
  const [progress, setProgress] = React.useState<number | null>(null);
  const [copyStatus, setCopyStatus] = React.useState<string>('Copy link to file');
  const multipartUploadHook = useCDSMultipartUpload(props.assignmentId, taskContext.workerId);

  const shouldUseCDSMultipartUpload = useUnleashFeatureFlag(
    'chat-enable-cds-multipart-upload',
    { projectId: taskContext.projectId },
    false,
  );

  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId: taskContext.projectId },
    false,
  );

  const transcriptionService = shouldUseWhisperTranscription
    ? createWhisperTranscriptionService()
    : createAwsTranscriptionService();

  const allowedMimeTypes = useMemo(
    () =>
      field.allowed_mime_types ?? [
        ...Object.values(SelfServeSupportedAttachmentMimeTypes),
        'application/zip',
        'application/xml',
      ],
    [field.allowed_mime_types],
  );

  const isSelectedAudioFile = selectedFile?.type?.startsWith('audio/');
  const targetAudioFormat: SupportedAudioFormat | undefined = useChatStoreExtract(
    state => state.config.audio?.format,
  );

  const handleCancel = useCallback(() => {
    setSelectedFile(null);
    setFetchedUrl('');
    setErrorMessage('');
    setIsUploadError(false);
    setAnnotation.current({
      ...annotation,
      response: [],
    });
    setCopyStatus('Copy link to file');
  }, [annotation, setAnnotation]);

  const handleAddFile = useCallback((files: File[]) => {
    const file = files[0];
    setSelectedFile(file);
  }, []);

  const handleUploadRetry = useCallback(async () => {
    // we do audio format conversion inside of the CDS multipart upload
    // todo: separation of concerns
    if (selectedFile && (shouldUseCDSMultipartUpload || isSelectedAudioFile)) {
      setErrorMessage('');
      setIsUploadError(false);
      setUploading(true);

      try {
        /**
         * file type that CDS will convert into (audio only right now)
         */
        const fileType: SupportedAudioFormat | undefined = isSelectedAudioFile
          ? targetAudioFormat
          : undefined;

        const { signedCdsUrl, s3Url, cdsUrl } = await multipartUploadHook.retryUpload({
          file: selectedFile,
          fileType,
          onProgress: (progress: number) => setProgress(progress),
          onError: (message: string) => {
            setErrorMessage(message);
            setIsUploadError(true);
            setProgress(null);
          },
          onComplete: () => setProgress(null),
        });

        let duration: number | undefined = undefined;
        if (isSelectedAudioFile) {
          try {
            await retry(
              () =>
                processAudioAttachment({
                  audioUrl: cdsUrl,
                  audioFormat: targetAudioFormat,
                  assignmentId: taskContext.assignmentId,
                  workerId: taskContext.workerId,
                  userGeneratedAudio: true,
                  transcriptionService,
                }),
              { limit: 3 },
            );
          } catch (error) {
            logger.error(`FileUploadFieldSection: Failed to start processing audio attachment`, {
              projectId: taskContext.projectId,
              assignmentId: taskContext.assignmentId,
              workerId: taskContext.workerId,
              error: formatMaybeErrorForLogging(error),
            });
          }

          const audioBuffer = await decodeAudioBuffer(await selectedFile.arrayBuffer());
          ({ duration } = audioBuffer ?? {});
        }

        setAnnotation.current({
          ...annotation,
          response: [
            {
              cdsUrl,
              signedUrl: signedCdsUrl,
              s3Url,
              // convert file extension if necessary
              name: fileType ? `${selectedFile.name}.${fileType}` : selectedFile.name,
              mimeType: selectedFile.type,
              duration,
            },
          ],
        });
      } catch (e) {
        setErrorMessage('Error uploading file');
      } finally {
        setUploading(false);
      }
    }
  }, [
    annotation,
    multipartUploadHook,
    selectedFile,
    shouldUseCDSMultipartUpload,
    setAnnotation,
    isSelectedAudioFile,
    targetAudioFormat,
    taskContext,
    transcriptionService,
  ]);

  const handleFileUpload = useCallback(async () => {
    if (selectedFile) {
      setErrorMessage('');
      setIsUploadError(false);
      setUploading(true);

      if (!isAllowedMimeType(selectedFile.type, allowedMimeTypes)) {
        setErrorMessage(
          `File type not allowed. Allowed file types: ${allowedMimeTypes.join(', ')}`,
        );
        setIsUploadError(false);
        setUploading(false);
        return;
      }

      try {
        // we do audio format conversion inside of the CDS multipart upload
        // todo: separation of concerns
        if (shouldUseCDSMultipartUpload || isSelectedAudioFile) {
          /**
           * file type that CDS will convert into (audio only right now)
           */
          const fileType: SupportedAudioFormat | undefined = isSelectedAudioFile
            ? targetAudioFormat
            : undefined;

          const { signedCdsUrl, s3Url, cdsUrl } = await multipartUploadHook.upload({
            file: selectedFile,
            fileType,
            onProgress: (progress: number) => setProgress(progress),
            onError: (message: string) => {
              setErrorMessage(message);
              setIsUploadError(true);
              setProgress(null);
            },
            onComplete: () => setProgress(null),
          });

          const { duration } =
            (isSelectedAudioFile && (await decodeAudioBuffer(await selectedFile.arrayBuffer()))) ||
            {};

          setAnnotation.current({
            ...annotation,
            response: [
              {
                cdsUrl,
                signedUrl: signedCdsUrl,
                s3Url,
                // convert file extension if necessary
                name: fileType ? `${selectedFile.name}.${fileType}` : selectedFile.name,
                mimeType: selectedFile.type,
                duration,
              },
            ],
          });
          if (isSelectedAudioFile) {
            try {
              // get bg noise data, transcripts, etc for their upload
              await retry(
                () =>
                  processAudioAttachment({
                    audioUrl: cdsUrl,
                    audioFormat: targetAudioFormat,
                    assignmentId: taskContext.assignmentId,
                    workerId: taskContext.workerId,
                    userGeneratedAudio: true,
                    transcriptionService,
                  }),
                { limit: 3 },
              );
            } catch (error) {
              logger.error(`FileUploadFieldSection: Failed to start processing audio attachment`, {
                projectId: taskContext.projectId,
                assignmentId: taskContext.assignmentId,
                workerId: taskContext.workerId,
                error: formatMaybeErrorForLogging(error),
              });
            }
          }
        } else {
          // TODO: replace key with project/task id
          // after doing so potentially pass in the project id
          // to this endpoint so that we don't have to populate
          // the project from the assignment anymore.
          const key = 'test';
          const formData = new FormData();
          formData.append('asset', selectedFile);
          formData.append('assignmentId', props.assignmentId ?? '');
          const uploadResponse = await apiFetch<ContentUnderstandingUploadResponseBody>(
            null,
            `internal/contentUnderstanding/fileUpload/${key}`,
            {
              method: 'POST',
              body: formData,
            },
          );
          if (uploadResponse.type === 'error') {
            uploadResponse.errors.forEach((message: string) => {
              toast.error(message, { position: 'bottom-right' });
            });
            return undefined;
          }
          const { url, s3Url } = uploadResponse;

          if (isSelectedAudioFile) {
            // audio files should never come down this path
            // if you need to change this, also calculate duration here
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _ensureNeverAudio: never = isSelectedAudioFile;
          }

          setAnnotation.current({
            ...annotation,
            response: [
              {
                signedUrl: url,
                s3Url,
                name: selectedFile.name,
                mimeType: selectedFile.type,
              },
            ],
          });
        }
      } catch (e) {
        setErrorMessage('Error uploading file');
      } finally {
        setUploading(false);
      }
    }
  }, [
    annotation,
    multipartUploadHook,
    allowedMimeTypes,
    props.assignmentId,
    selectedFile,
    setAnnotation,
    shouldUseCDSMultipartUpload,
    isSelectedAudioFile,
    targetAudioFormat,
    taskContext,
    transcriptionService,
  ]);

  const handleFileDownload = useCallback(async () => {
    if (fetchedUrl.length > 0 && annotation.response[0].name && annotation.response[0].mimeType) {
      try {
        setErrorMessage('');
        setDownloading(true);
        try {
          const isAudioFile = annotation.response[0].mimeType.startsWith('audio/');

          // we have a special function get get audio files
          // this is currently just to avoid fetching stale data from the CDN
          const url = isAudioFile
            ? (
                await getAudioSource({
                  audioUrl: annotation.response[0].cdsUrl || annotation.response[0].s3Url,
                })
              ).source
            : (
                await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
                  method: 'POST',
                  body: {
                    url: annotation.response[0].cdsUrl || annotation.response[0].s3Url,
                  },
                })
              ).url;

          const response = await fetch(url, {
            headers: { 'Content-Type': annotation.response[0].mimeType },
          });
          if (!response || !response.body) {
            logger.error('Failed to download file');
            return;
          }
          const reader = response.body.getReader();
          const stream = new ReadableStream({
            start(controller) {
              function push() {
                reader
                  .read()
                  .then(({ done, value }) => {
                    if (done) {
                      controller.close();
                      return; // Properly returning from the `then()`
                    }
                    controller.enqueue(value);
                    return push(); // Return the next call to push()
                  })
                  .catch(err => {
                    logger.error('Stream reading failed:', err);
                    controller.error(err); // Report any errors to the stream
                    throw err; // Ensure the catch block throws an error
                  });
              }
              push();
            },
          });
          const blob = await new Response(stream).blob();
          FileSaver.saveAs(blob, annotation.response[0].name);
        } catch {
          // Try the old way if the new way fails
          const { body } = await apiFetch(null, `internal/contentUnderstanding/fileDownload`, {
            method: 'POST',
            body: {
              url: annotation.response[0].cdsUrl || annotation.response[0].s3Url,
            },
          });
          const blob = new Blob([new Uint8Array(body.data)], {
            type: annotation.response[0].mimeType,
          });
          FileSaver.saveAs(blob, annotation.response[0].name);
        }
      } catch (e) {
        setErrorMessage('Error downloading file');
      }
      setDownloading(false);
    }
  }, [annotation.response, fetchedUrl.length]);

  const handleFileFetch = useCallback(async () => {
    if (annotation.response.length) {
      try {
        setErrorMessage('');
        const { url } = await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
          method: 'POST',
          body: {
            url: annotation.response[0].cdsUrl || annotation.response[0].s3Url,
          },
        });
        if (annotation.response[0].mimeType === 'text/plain') {
          const resp = await fetch(url);
          const text = await resp.text();
          setTextAttachmentContent(text);
        } else if (annotation.response[0].mimeType === 'application/json') {
          const resp = await fetch(url);
          const text = await resp.json();
          setTextAttachmentContent(text);
        }
        setFetchedUrl(url);
      } catch (e) {
        setErrorMessage('Error fetching file');
      }
    }
  }, [annotation.response]);

  const handleCopyLink = async () => {
    try {
      let url;
      try {
        const isAudioFile = annotation.response[0].mimeType?.startsWith('audio/');
        url = isAudioFile
          ? (
              await getAudioSource({
                audioUrl: annotation.response[0].cdsUrl || annotation.response[0].s3Url,
              })
            ).source
          : (
              await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
                method: 'POST',
                body: {
                  url: annotation.response[0].cdsUrl || annotation.response[0].s3Url,
                },
              })
            ).url;
      } catch {
        url = annotation.response[0].signedUrl;
      }
      await navigator.clipboard.writeText(url);
      setCopyStatus('Copied!');
    } catch (err) {
      setErrorMessage('Failed to copy');
    }
  };

  useEffect(() => {
    if (selectedFile) {
      shouldUseCDSMultipartUpload && setProgress(0);
      handleFileUpload();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, shouldUseCDSMultipartUpload]);

  useEffect(() => {
    if (annotation.response.length && !fetchedUrl.length) {
      handleFileFetch();
    }
  }, [annotation.response.length, fetchedUrl.length, handleFileFetch]);

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader title={field.title} description={field.description} hint={field.hint} />
      {
        <>
          {selectedFile || annotation.response.length > 0 ? (
            <>
              {annotation.response.length && annotation.response[0].name ? (
                <>
                  <Typography.Paragraph2>
                    Filename: {annotation.response[0].name}
                  </Typography.Paragraph2>
                  <div className="flex flex-row gap-2 items-center">
                    <Button
                      variant={copyStatus === 'Copied!' ? 'success' : 'secondary'}
                      onClick={handleCopyLink}
                    >
                      {copyStatus}
                    </Button>
                  </div>
                </>
              ) : selectedFile ? (
                <>
                  <Typography.Paragraph2>Filename: {selectedFile.name}</Typography.Paragraph2>
                </>
              ) : (
                <></>
              )}
              {progress != null && (
                <div className="flex flex-col items-center">
                  <Typography.Paragraph2 className="font-mono font-semibold">
                    {Math.round(progress)}%
                  </Typography.Paragraph2>
                  <ProgressStepper size="sm" steps={100} stepsCompleted={progress} />
                </div>
              )}
              <div className="mt-2 flex flex-row gap-2 items-center">
                {!disabled && (
                  <>
                    <Button onClick={() => handleCancel()}>Cancel</Button>
                    {uploading && <Spinner size="sm" />}
                  </>
                )}
                {fetchedUrl.length > 0 &&
                  annotation.response.length > 0 &&
                  annotation.response[0].mimeType && (
                    <Button onClick={() => handleFileDownload()}>
                      Download File
                      {downloading && <Spinner size="sm" />}
                    </Button>
                  )}
              </div>
            </>
          ) : (
            <FileUpload allowedMimeTypes={allowedMimeTypes.join(',')} onFiles={handleAddFile} />
          )}
        </>
      }
      {fetchedUrl.length > 0 &&
        annotation.response.length > 0 &&
        annotation.response[0].mimeType && (
          <div className="mt-2">
            {fieldSectionAttachmentView(
              fetchedUrl,
              annotation.response[0].mimeType,
              textAttachmentContent,
            )}
          </div>
        )}
      {errorMessage && <div className="text-danger-500">{errorMessage}</div>}
      {isUploadError && shouldUseCDSMultipartUpload && (
        <Button
          className="w-36"
          onClick={() => {
            handleUploadRetry();
          }}
        >
          {' '}
          Retry Upload From Last Checkpoint{' '}
        </Button>
      )}
    </div>
  );
}
